import React, { useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  nextStep,
  prevStep,
  showValidationErrors as showValidationErrorsAction,
} from "../../store/slices/navigationSlice";
import {
  validateStep1,
  validateStep2,
  validateStep3,
  validateStep4,
} from "../../utils/stepValidation";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";
import { createCompleteProject } from "../../services/salesforceProjectApi";
import { resetCreation as resetProjectCreation } from "../../store/slices/projectSlice";
import { resetCreation as resetObjectiveCreation } from "../../store/slices/projectObjectiveSlice";
import { resetCreation as resetQualificationStepCreation } from "../../store/slices/qualificationStepSlice";
import { resetCreation as resetPageCreation } from "../../store/slices/pageSlice";
import { resetCreation as resetComponentCreation } from "../../store/slices/componentSlice";
import { persistor } from "../../store";
const WizardNavigation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentStep = useSelector((state) => state.navigation.currentStep);
  const totalSteps = useSelector((state) => state.navigation.totalSteps);
  const metadata = useSelector((state) => state.project.creation.metadata);
  const objectives = useSelector(
    (state) => state.projectObjective.creation.objectives,
  );
  const qualificationSteps = useSelector(
    (state) => state.qualificationStep.creation.qualificationSteps,
  );
  const pages = useSelector((state) => state.page.creation.pages);
  const components = useSelector(
    (state) => state.component?.creation?.components ?? [],
  );

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Construct projectData object for backward compatibility with createCompleteProject
  const projectData = useMemo(
    () => ({
      metadata,
      objectives,
      qualificationSteps,
      pages,
      components,
    }),
    [metadata, objectives, qualificationSteps, pages, components],
  );

  // Compute validation fresh each time - use useMemo to avoid unnecessary recomputation
  const step1Validation = useMemo(() => validateStep1(metadata), [metadata]);
  const step2Validation = useMemo(
    () => validateStep2(objectives),
    [objectives],
  );
  const step3Validation = useMemo(
    () => validateStep3(qualificationSteps, metadata),
    [qualificationSteps, metadata],
  );
  const step4Validation = useMemo(() => validateStep4(pages), [pages]);

  const handleNext = () => {
    // Check current step validation and show errors if invalid
    // Compute validation fresh to ensure we have the latest data
    let currentValidation = { isValid: true, errors: [] }; // Default: valid for steps without validation
    console.log("PROJECTDATANAV", projectData);
    if (currentStep === 1) {
      currentValidation = validateStep1(metadata);
    } else if (currentStep === 2) {
      // Debug: Log objectives before validation
      console.log("🔍 Validating Step 2 - Objectives:", objectives);
      console.log("🔍 Objectives count:", objectives?.length);
      console.log("🔍 Objectives array:", JSON.stringify(objectives, null, 2));

      // Additional safety check: ensure we have objectives
      if (!objectives || objectives.length === 0) {
        console.log("❌ No objectives found, blocking navigation");
        currentValidation = {
          isValid: false,
          errors: ["Please add at least 1 Project Objective"],
        };
      } else {
        // Check each objective has workType - be thorough
        const objectivesWithoutWorkType = objectives.filter((obj) => {
          const workType = obj.workType;
          const isEmpty =
            workType === undefined ||
            workType === null ||
            workType === "" ||
            (typeof workType === "string" && workType.trim() === "");
          return isEmpty;
        });

        if (objectivesWithoutWorkType.length > 0) {
          console.log(
            "⚠️ Found objectives without workType:",
            objectivesWithoutWorkType,
          );
          console.log(
            "⚠️ Objectives without workType details:",
            objectivesWithoutWorkType.map((obj) => ({
              id: obj.id,
              objectiveName: obj.objectiveName,
              workType: obj.workType,
              workTypeType: typeof obj.workType,
              hasWorkType: obj.hasOwnProperty("workType"),
            })),
          );
        }

        currentValidation = validateStep2(objectives);
        console.log("✅ Step 2 Validation Result:", currentValidation);

        // Double-check: if validation passed but we found objectives without workType, force invalid
        if (currentValidation.isValid && objectivesWithoutWorkType.length > 0) {
          console.log(
            "⚠️ Validation passed but found objectives without workType - forcing invalid",
          );
          currentValidation = {
            isValid: false,
            errors: [
              ...currentValidation.errors,
              ...objectivesWithoutWorkType.map(
                (obj, idx) =>
                  `Objective ${objectives.indexOf(obj) + 1}: Work Type is required`,
              ),
            ],
          };
        }
      }
    } else if (currentStep === 3) {
      currentValidation = validateStep3(qualificationSteps, metadata);
    } else if (currentStep === 4) {
      currentValidation = validateStep4(pages);
    }
    // Steps 5 and 6 have no validation - always valid

    // CRITICAL: Double-check for step 2 - if any objective is missing workType, force invalid
    if (currentStep === 2 && objectives && objectives.length > 0) {
      const hasMissingWorkType = objectives.some((obj) => {
        const wt = obj.workType;
        return (
          wt === undefined ||
          wt === null ||
          wt === "" ||
          (typeof wt === "string" && wt.trim() === "")
        );
      });

      if (hasMissingWorkType) {
        console.log(
          "🚫 CRITICAL: Found objectives with missing workType, forcing validation to fail",
        );
        currentValidation = {
          isValid: false,
          errors: [
            ...(currentValidation.errors || []),
            ...objectives
              .map((obj, idx) => {
                const wt = obj.workType;
                const isEmpty =
                  wt === undefined ||
                  wt === null ||
                  wt === "" ||
                  (typeof wt === "string" && wt.trim() === "");
                return isEmpty
                  ? `Objective ${idx + 1}: Work Type is required`
                  : null;
              })
              .filter(Boolean),
          ],
        };
      }

      // CRITICAL: Double-check for step 2 - if any objective is missing both country and language, force invalid
      const hasMissingCountryAndLanguage = objectives.some((obj) => {
        const country = obj.country || "";
        const language = obj.language || "";
        const countryIsEmpty =
          country === undefined ||
          country === null ||
          country === "" ||
          (typeof country === "string" && country.trim() === "");
        const languageIsEmpty =
          language === undefined ||
          language === null ||
          language === "" ||
          (typeof language === "string" && language.trim() === "");
        return countryIsEmpty && languageIsEmpty;
      });

      if (hasMissingCountryAndLanguage) {
        console.log(
          "🚫 CRITICAL: Found objectives with missing country and language, forcing validation to fail",
        );
        currentValidation = {
          isValid: false,
          errors: [
            ...(currentValidation.errors || []),
            ...objectives
              .map((obj, idx) => {
                const country = obj.country || "";
                const language = obj.language || "";
                const countryIsEmpty =
                  country === undefined ||
                  country === null ||
                  country === "" ||
                  (typeof country === "string" && country.trim() === "");
                const languageIsEmpty =
                  language === undefined ||
                  language === null ||
                  language === "" ||
                  (typeof language === "string" && language.trim() === "");
                return countryIsEmpty && languageIsEmpty
                  ? `Objective ${idx + 1}: Either Country or Language must be filled`
                  : null;
              })
              .filter(Boolean),
          ],
        };
      }
    }

    if (!currentValidation?.isValid) {
      console.log(
        "❌ Validation failed, blocking navigation. Errors:",
        currentValidation.errors,
      );
      console.log("❌ Validation isValid:", currentValidation.isValid);
      console.log(
        "❌ Validation errors count:",
        currentValidation.errors?.length,
      );
      // Mark that user attempted to proceed - show validation errors
      dispatch(showValidationErrorsAction());
      // Prevent navigation
      return;
    }

    console.log("✅ Validation passed, proceeding to next step");
    // Validation passed - proceed to next step
    dispatch(nextStep());
  };

  const handlePrevious = () => {
    dispatch(prevStep());
  };

  const handleFinish = async () => {
    setIsSaving(true);
    setSaveError(null);

    try {
      console.log("Starting project save to Salesforce...", projectData);

      // Call Salesforce API to create project and objectives
      const result = await createCompleteProject(projectData);

      console.log("Salesforce save result:", result);

      if (result.success) {
        // Save to localStorage with Salesforce IDs
        const projectToSave = {
          ...projectData,
          id: result.projectId, // Use Salesforce ID as primary ID
          salesforceId: result.projectId,
          savedAt: new Date().toISOString(),
          syncedWithSalesforce: true,
        };

        // Get existing projects from localStorage
        const existingProjects = JSON.parse(
          localStorage.getItem("projects") || "[]",
        );

        // Check if project already exists (update) or is new (add)
        const projectIndex = existingProjects.findIndex(
          (p) =>
            p.salesforceId === result.projectId ||
            p.id === projectData.metadata?.id,
        );

        if (projectIndex >= 0) {
          // Update existing project
          existingProjects[projectIndex] = projectToSave;
        } else {
          // Add new project
          existingProjects.push(projectToSave);
        }

        // Save back to localStorage
        localStorage.setItem("projects", JSON.stringify(existingProjects));
        localStorage.setItem("currentProject", JSON.stringify(projectToSave));

        // Show success message
        alert(
          `✅ Success! Project created in Salesforce!\n\n` +
            `Project ID: ${result.projectId}\n` +
            `Objectives Created: ${result.objectives.length}\n\n` +
            `${result.message}`,
        );

        dispatch(resetProjectCreation());
        dispatch(resetObjectiveCreation());
        dispatch(resetQualificationStepCreation());
        dispatch(resetPageCreation());
        dispatch(resetComponentCreation());

        await persistor.flush();
        navigate(`/p/${result.projectId}`);
      }
    } catch (error) {
      console.error("Error saving project to Salesforce:", error);

      const errorMessage =
        error.response?.data?.error?.message ||
        error.message ||
        "Unknown error";
      setSaveError(errorMessage);

      // Fallback: Save to localStorage only if Salesforce fails
      try {
        const projectToSave = {
          ...projectData,
          id: projectData.metadata?.id || Date.now(),
          savedAt: new Date().toISOString(),
          syncedWithSalesforce: false,
          salesforceError: errorMessage,
        };

        localStorage.setItem("currentProject", JSON.stringify(projectToSave));

        alert(
          `⚠️ Warning: Could not save to Salesforce\n\n` +
            `Error: ${errorMessage}\n\n` +
            `Data has been saved locally only. Please check your Salesforce connection and try again.`,
        );
      } catch (localError) {
        alert(
          "❌ Error: Could not save project to Salesforce or locally. Please try again.",
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-gray-50 border-t border-gray-200">
      <div className="flex items-center justify-between px-6 py-4">
        <button
          onClick={handlePrevious}
          disabled={currentStep === 1}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            currentStep === 1
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
          }`}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2">
          {Array.from({ length: totalSteps }, (_, i) => (
            <div
              key={i}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                i + 1 <= currentStep
                  ? "text-white"
                  : "bg-gray-200 text-gray-600"
              }`}
              style={i + 1 <= currentStep ? { backgroundColor: "#08979c" } : {}}
            >
              {i + 1 < currentStep ? <Check className="w-4 h-4" /> : i + 1}
            </div>
          ))}
        </div>

        {currentStep < totalSteps ? (
          <button
            onClick={handleNext}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors text-white"
            style={{ backgroundColor: "#08979c" }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#0ab0b7")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "#08979c")
            }
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={handleFinish}
            disabled={isSaving}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-colors ${
              isSaving
                ? "bg-gray-400 text-white cursor-not-allowed"
                : "text-white"
            }`}
            style={isSaving ? {} : { backgroundColor: "#08979c" }}
            onMouseEnter={(e) =>
              !isSaving && (e.currentTarget.style.backgroundColor = "#0ab0b7")
            }
            onMouseLeave={(e) =>
              !isSaving && (e.currentTarget.style.backgroundColor = "#08979c")
            }
          >
            {isSaving ? (
              <>
                <svg
                  className="animate-spin w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Saving to Salesforce...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Finish Setup
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default WizardNavigation;
